import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { handleSupabaseError } from '../lib/errors'
import { PlanItem, CreatePlanItem, UpdatePlanItem } from '../types'

export function usePlanItems(runId: string) {
  return useQuery({
    queryKey: ['plan_items', runId],
    queryFn: async (): Promise<PlanItem[]> => {
      const { data, error } = await supabase
        .from('plan_items')
        .select('*')
        .eq('run_id', runId)
        .order('created_at')
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    enabled: !!runId
  })
}

export function usePlanItemsBySegment(segmentId: string) {
  return useQuery({
    queryKey: ['plan_items', 'segment', segmentId],
    queryFn: async (): Promise<PlanItem[]> => {
      const { data, error } = await supabase
        .from('plan_items')
        .select('*')
        .eq('segment_id', segmentId)
        .order('created_at')
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    enabled: !!segmentId
  })
}

export function usePlanItemsByAidStation(aidStationId: string) {
  return useQuery({
    queryKey: ['plan_items', 'aid_station', aidStationId],
    queryFn: async (): Promise<PlanItem[]> => {
      const { data, error } = await supabase
        .from('plan_items')
        .select('*')
        .eq('aid_station_id', aidStationId)
        .order('created_at')
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    enabled: !!aidStationId
  })
}

export function useCreatePlanItem() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (newPlanItem: CreatePlanItem): Promise<PlanItem> => {
      const { data, error } = await supabase
        .from('plan_items')
        .insert(newPlanItem)
        .select()
        .single()
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['plan_items', data.run_id] })
      queryClient.invalidateQueries({ queryKey: ['runs', data.run_id] })
      
      if (data.segment_id) {
        queryClient.invalidateQueries({ queryKey: ['plan_items', 'segment', data.segment_id] })
      }
      
      if (data.aid_station_id) {
        queryClient.invalidateQueries({ queryKey: ['plan_items', 'aid_station', data.aid_station_id] })
      }
    }
  })
}

export function useUpdatePlanItem() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdatePlanItem }): Promise<PlanItem> => {
      const { data, error } = await supabase
        .from('plan_items')
        .update(updates)
        .eq('id', id)
        .select()
        .single()
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['plan_items', data.run_id] })
      queryClient.invalidateQueries({ queryKey: ['runs', data.run_id] })
      
      if (data.segment_id) {
        queryClient.invalidateQueries({ queryKey: ['plan_items', 'segment', data.segment_id] })
      }
      
      if (data.aid_station_id) {
        queryClient.invalidateQueries({ queryKey: ['plan_items', 'aid_station', data.aid_station_id] })
      }
    }
  })
}

export function useDeletePlanItem() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (id: string): Promise<PlanItem> => {
      // Get the plan item first to know what to invalidate
      const { data: planItem } = await supabase
        .from('plan_items')
        .select('*')
        .eq('id', id)
        .single()
      
      const { error } = await supabase
        .from('plan_items')
        .delete()
        .eq('id', id)
      
      if (error) throw handleSupabaseError(error)
      return planItem!
    },
    onSuccess: (planItem) => {
      queryClient.invalidateQueries({ queryKey: ['plan_items', planItem.run_id] })
      queryClient.invalidateQueries({ queryKey: ['runs', planItem.run_id] })
      
      if (planItem.segment_id) {
        queryClient.invalidateQueries({ queryKey: ['plan_items', 'segment', planItem.segment_id] })
      }
      
      if (planItem.aid_station_id) {
        queryClient.invalidateQueries({ queryKey: ['plan_items', 'aid_station', planItem.aid_station_id] })
      }
    }
  })
}